import { api } from './api'
import { dropThisBrowserPush } from './pushLocal'
import { isIOS, isStandaloneDisplay } from './pwaInstall'

// VAPID public keys are base64url; PushManager.subscribe wants a Uint8Array.
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i)
  }
  return output
}

export function isPushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  )
}

// On iOS, Web Push only exists for installed PWAs (home-screen app). The platform checks are
// shared with the «install the app» card.
const isStandalone = isStandaloneDisplay

// serviceWorker.ready never rejects: if no service worker is registered (e.g. the
// PWA plugin is disabled) it just waits forever. Cap the wait so callers can fail
// visibly instead of hanging the UI.
async function getReadyRegistration(timeoutMs = 5000): Promise<ServiceWorkerRegistration | null> {
  const timeout = new Promise<null>((resolve) => {
    setTimeout(() => resolve(null), timeoutMs)
  })
  return Promise.race([navigator.serviceWorker.ready, timeout])
}

// PushAvailability distinguishes WHY push can or cannot be enabled in this browser,
// so the UI can show an honest, actionable message instead of a silent dead toggle.
export type PushAvailability = 'ready' | 'denied' | 'needs-ios-install' | 'unsupported'

export function getPushAvailability(): PushAvailability {
  if (isPushSupported()) {
    if (typeof Notification !== 'undefined' && Notification.permission === 'denied') return 'denied'
    return 'ready'
  }
  // Push API missing: on an un-installed iOS Safari this is fixable by installing the PWA.
  if (isIOS() && !isStandalone()) return 'needs-ios-install'
  return 'unsupported'
}

// pushAvailabilityMessage is the user-facing explanation for a given availability state.
export function pushAvailabilityMessage(status: PushAvailability): string {
  switch (status) {
    case 'ready':
      return 'Уведомления о ваших заявках придут в браузер, даже когда вкладка закрыта.'
    case 'denied':
      return 'Уведомления заблокированы для этого сайта. Нажмите на переключатель — подскажем, как их разрешить.'
    case 'needs-ios-install':
      return 'На iPhone и iPad push работает только в установленном приложении. Откройте сайт в Safari → «Поделиться» → «На экран „Домой“», затем включите уведомления из установленного приложения.'
    case 'unsupported':
      return 'Ваш браузер не поддерживает push-уведомления.'
  }
}

// pushBlockedHelp says how to lift a block the site cannot lift itself: once notifications are
// blocked, requestPermission answers «denied» at once and shows nothing, so only the browser's own
// site settings can undo it. The steps differ per browser, hence the sniffing.
export function pushBlockedHelp(): string {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent
  if (isIOS()) {
    return 'Уведомления запрещены в настройках iPhone. Откройте «Настройки» → «Уведомления», найдите установленное приложение сайта, включите «Допуск уведомлений» и снова нажмите на переключатель.'
  }
  if (/Firefox|FxiOS/.test(ua)) {
    return 'Уведомления запрещены в браузере. Нажмите на значок слева от адреса сайта, уберите блокировку уведомлений (крестик) и снова нажмите на переключатель.'
  }
  const isSafari = /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR|YaBrowser/.test(ua)
  if (isSafari) {
    return 'Уведомления запрещены в Safari. Откройте Safari → «Настройки» → «Веб-сайты» → «Уведомления», выберите «Разрешить» для этого сайта и снова нажмите на переключатель.'
  }
  return 'Уведомления запрещены в браузере. Нажмите на значок слева от адреса сайта, включите «Уведомления» и снова нажмите на переключатель.'
}

// watchPushAvailability calls onChange whenever the notification permission may have changed —
// the user unblocks the site in the browser's settings while the page stays open. Chrome and
// Firefox report it through the Permissions API; Safari does not, so coming back to the tab
// re-checks too. Returns the cleanup.
export function watchPushAvailability(onChange: (status: PushAvailability) => void): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {}
  const check = () => onChange(getPushAvailability())
  let permission: PermissionStatus | null = null
  let stopped = false

  try {
    navigator.permissions
      ?.query({ name: 'notifications' as PermissionName })
      .then((status) => {
        if (stopped) return
        permission = status
        status.addEventListener('change', check)
      })
      .catch(() => {})
  } catch {
    // Older browsers throw on an unknown permission name; focus/visibility still cover them.
  }
  window.addEventListener('focus', check)
  document.addEventListener('visibilitychange', check)

  return () => {
    stopped = true
    permission?.removeEventListener('change', check)
    window.removeEventListener('focus', check)
    document.removeEventListener('visibilitychange', check)
  }
}

// enablePush requests permission, subscribes this browser, and registers the
// subscription with the backend. Returns false if push is unsupported, disabled
// server-side, or the user denied permission.
//
// Call it straight from the click handler with nothing awaited before it. Safari (iPhone and Mac)
// shows the permission prompt only while the click's user activation is still live; waiting for
// the VAPID key first used it up, and WebKit then silently skipped the prompt — the switch just
// flipped back. So the permission is asked first, and the key fetched after.
export async function enablePush(): Promise<boolean> {
  if (!isPushSupported()) return false

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return false

  const { enabled, public_key } = await api.getPushVapidKey()
  if (!enabled || !public_key) return false

  const registration = await getReadyRegistration()
  if (!registration) return false
  let subscription = await registration.pushManager.getSubscription()
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key),
    })
  }

  await registerSubscription(subscription)
  return true
}

async function registerSubscription(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON()
  await api.subscribePush({
    endpoint: subscription.endpoint,
    keys: {
      p256dh: json.keys?.p256dh ?? '',
      auth: json.keys?.auth ?? '',
    },
  })
}

// reconcilePush aligns THIS browser's actual subscription with the saved preference,
// without ever prompting the user. It returns the effective on/off state for this
// browser, so the UI can show the toggle honestly:
//   - preference off  -> ensures this browser is unsubscribed, returns false
//   - preference on + already subscribed -> re-registers (idempotent), returns true
//   - preference on + no subscription but permission granted -> self-heals
//     (silently re-subscribes, e.g. after the browser dropped the subscription)
//   - preference on but permission not granted -> returns false (can't receive here)
export async function reconcilePush(preferenceEnabled: boolean): Promise<boolean> {
  if (!isPushSupported()) return false

  const registration = await getReadyRegistration()
  if (!registration) return false
  const existing = await registration.pushManager.getSubscription()

  if (!preferenceEnabled) {
    if (existing) {
      await api.unsubscribePush({ endpoint: existing.endpoint }).catch(() => {})
      await existing.unsubscribe().catch(() => {})
    }
    return false
  }

  if (existing) {
    await registerSubscription(existing).catch(() => {})
    return true
  }

  if (Notification.permission !== 'granted') return false

  try {
    const { enabled, public_key } = await api.getPushVapidKey()
    if (!enabled || !public_key) return false
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key),
    })
    await registerSubscription(subscription)
    return true
  } catch {
    return false
  }
}

// forgetPushOnSignOut stops this browser receiving the pushes of the account that signs out: on a
// shared computer the next person must not see the previous one's shipments. The preference stays
// on the server, and restorePushAfterSignIn brings the subscription back on the next sign-in.
// tellServer is false when the account is already gone (the server dropped its rows itself).
export async function forgetPushOnSignOut({ tellServer }: { tellServer: boolean }): Promise<void> {
  await dropThisBrowserPush(tellServer ? (endpoint) => api.forgetPushEndpoint(endpoint) : undefined)
}

// restorePushAfterSignIn re-subscribes this browser after a sign-in when the account wants pushes
// and the browser already allows them — without a prompt. It also hands a subscription left over
// from someone else's session to this account, or drops it when this account wants none. Does
// nothing where notifications were never allowed, so it never asks anybody anything.
export async function restorePushAfterSignIn(): Promise<boolean> {
  if (!isPushSupported() || Notification.permission !== 'granted') return false
  try {
    const settings = await api.getNotificationSettings()
    return await reconcilePush(settings.push_enabled)
  } catch {
    return false
  }
}

// disablePush unsubscribes this browser and tells the backend to drop the endpoint.
export async function disablePush(): Promise<void> {
  if (!isPushSupported()) return

  const registration = await getReadyRegistration()
  if (!registration) return
  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) return

  await api.unsubscribePush({ endpoint: subscription.endpoint }).catch(() => {})
  await subscription.unsubscribe().catch(() => {})
}
